/** Strict, signed amounts for the one-line-per-kind BC settlement contract. */
export type SettlementKind = "Charge" | "Deduction";

export type SettlementLine = {
  systemId: string;
  directUnitCost: number | null;
  quantity: number | null;
  settlementAmountEditable: boolean;
  lineMatched: boolean;
};

export type SettlementPlan =
  | { action: "create"; amount: number }
  | { action: "update"; systemId: string; directUnitCost: number };

/** The editor accepts a positive magnitude; the sign comes from the kind. */
export function parseSettlementMagnitude(kind: SettlementKind, raw: string): number {
  const normalized = raw.trim().replace(",", ".");
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
    throw new Error("Podaj dodatnią kwotę z maksymalnie dwoma miejscami po przecinku.");
  }
  const magnitude = Number(normalized);
  const cents = Math.round(magnitude * 100);
  if (!Number.isFinite(magnitude) || !Number.isSafeInteger(cents) || cents < 1) {
    throw new Error("Podaj poprawną kwotę większą od zera.");
  }
  return (kind === "Deduction" ? -1 : 1) * cents / 100;
}

export function isValidSettlementAmount(kind: SettlementKind, amount: number): boolean {
  const cents = Math.round(amount * 100);
  return Number.isFinite(amount) && Number.isSafeInteger(cents) &&
    Math.abs(cents / 100 - amount) < 0.0000001 &&
    (kind === "Charge" ? cents >= 1 : cents <= -1);
}

/** No redistribution, no extra BC lines when a row already exists. */
export function planSettlementChange(
  kind: SettlementKind,
  kindLines: SettlementLine[],
  amount: number,
  allowCreate: boolean,
): SettlementPlan {
  if (!isValidSettlementAmount(kind, amount)) {
    throw new Error(kind === "Charge"
      ? "Kwota obciążenia musi być większa od zera."
      : "Kwota odliczenia musi być mniejsza od zera.");
  }
  if (kindLines.length > 1) {
    throw new Error(
      "Dokument ma kilka wierszy tego typu. Przed edycją uporządkuj je w Business Central."
    );
  }
  if (kindLines.length === 0) {
    if (!allowCreate) {
      throw new Error("Dodawanie wiersza tego typu jest zablokowane w Business Central.");
    }
    return { action: "create", amount };
  }
  const line = kindLines[0];
  if (!line.settlementAmountEditable || line.lineMatched || Number(line.quantity ?? 1) !== 1) {
    throw new Error("Business Central nie pozwala zmienić kwoty tego wiersza.");
  }
  return { action: "update", systemId: line.systemId, directUnitCost: amount };
}
